"use client";

import { usePathname } from "next/navigation";
import React, { useState, useEffect, useCallback } from "react";
import HomepageCarousel, {
  SlideProps,
} from "@/components/HomepageCarousel/HomepageCarousel";
import StandardModal from "@/components/StandardModal/StandardModal";
import StandardNavigation from "@/components/StandardNavigation/StandardNavigation";
import PromoCta from "@/components/PromoCta/PromoCta";

type HomepageContent = {
  carouselSlidesUrl: string;
  promoCta: {
    imageUrl: string;
    buttonText: string;
    headingText: string;
    modalId: string;
  };
  videoSrc: string;
  slideHeight: string;
};

export const HomePage: React.FC = () => {
  const pathname = usePathname();
  const [slides, setSlides] = useState<SlideProps[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string>("");
  const [isPlaying, setIsPlaying] = useState<boolean>(true);
  const [content, setContent] = useState<HomepageContent | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchContent = useCallback(async (): Promise<HomepageContent | undefined> => {
    try {
      const response = await fetch("/content/homepage-content.json");
      if (!response.ok) {
        throw new Error("Failed to fetch content");
      }
      const data: HomepageContent = await response.json();
      setContent(data);
      return data;
    } catch (error: unknown) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError("An unknown error occurred");
      }
      console.error("Error fetching content:", error);
    }
  }, []);

  const fetchSlides = useCallback(async (url: string): Promise<void> => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to fetch slides");
      }
      const data: SlideProps[] = await response.json();
      setSlides(data);
    } catch (error: unknown) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError("An unknown error occurred");
      }
      console.error("Error fetching slides:", error);
    }
  }, []);

  useEffect(() => {
    const initializeContent = async () => {
      const data = await fetchContent();
      if (data) {
        fetchSlides(data.carouselSlidesUrl);
      }
    };
    initializeContent();
  }, [fetchContent, fetchSlides]);

  const openModal = (modalId: string): void => {
    setModalType(modalId);
    setIsModalOpen(true);
    setIsPlaying(false);
  };

  const closeModal = (): void => {
    setIsModalOpen(false);
    setIsPlaying(true);
  };

  const handlePromoButtonClick = (): void => {
    if (content) {
      openModal(content.promoCta.modalId);
    }
  };

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    content && (
      <main className="flex h-svh w-svw flex-col items-center justify-center">
        <StandardNavigation pathname={pathname} openModal={openModal} />
        <HomepageCarousel
          classname="homepage-carousel"
          slides={slides}
          onCtaClick={openModal}
          isPlaying={isPlaying}
          setIsPlaying={setIsPlaying}
          slideHeight={content.slideHeight}
        />
        <StandardModal
          customClassName="standard-modal-homepage"
          isOpen={isModalOpen}
          onClose={closeModal}
          modalType={modalType}
        />
        <PromoCta
          imageUrl={content.promoCta.imageUrl}
          buttonText={content.promoCta.buttonText}
          headingtext={content.promoCta.headingText}
          onButtonClick={handlePromoButtonClick}
        />
      </main>
    )
  );
};
